import omit from 'lodash-es/omit';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  useCreateEmployeeDependentMutation,
  useDeleteEmployeeDependentMutation,
  useUpdateEmployeeDependentMutation,
} from '@/common/graphql';
import type { EmployeeDependentType } from '@/common/types';
import { ApiUtils } from '@/common/utils';

interface DependentPersistence {
  employeeId: string;
  dependents: EmployeeDependentType[];
  onDependentsChange: (data: EmployeeDependentType[]) => void;
  // this triggers Mutation to be triggered, otherwise we let user
  // handle manually the data that was modified (e.g. to be saved later)
  autoPersistInDatabase?: boolean;
}

export const useDependentPersistence = ({
  employeeId,
  dependents = [],
  onDependentsChange,
  autoPersistInDatabase = true,
}: DependentPersistence) => {
  const [createRecord] = useCreateEmployeeDependentMutation();
  const [updateRecord] = useUpdateEmployeeDependentMutation();
  const [deleteRecord] = useDeleteEmployeeDependentMutation();

  const onDependentCreate = useCallback(
    async ({ ...formData }: EmployeeDependentType) => {
      if (autoPersistInDatabase) {
        await createRecord({
          variables: {
            input: {
              ...omit(formData, 'id', 'age'),
              employeeId,
              relation: formData.relation,
            },
          },
          onCompleted: data => {
            toast.success();
            onDependentsChange([
              ...dependents,
              {
                ...formData,
                id: data.createEmployeeDependent.id,
                age: data.createEmployeeDependent.dependent.age,
              },
            ]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onDependentsChange([...dependents, { ...formData, id: uuid() }]);
      }
    },
    [autoPersistInDatabase, dependents, createRecord, employeeId, onDependentsChange],
  );

  const onDependentUpdate = useCallback(
    async (formData: EmployeeDependentType) => {
      if (autoPersistInDatabase) {
        await updateRecord({
          variables: {
            input: {
              ...omit(formData, 'age'),
              relation: formData.relation,
            },
          },
          onCompleted: data => {
            toast.success();
            onDependentsChange([
              ...dependents.map(item =>
                item.id === formData.id
                  ? {
                      ...item,
                      ...formData,
                      age: data.updateEmployeeDependent.dependent.age,
                    }
                  : item,
              ),
            ]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onDependentsChange([...dependents.map(item => (item.id === formData.id ? { ...item, ...formData } : item))]);
      }
    },
    [autoPersistInDatabase, dependents, onDependentsChange, updateRecord],
  );

  const onDependentRemove = useCallback(
    async (id: string) => {
      if (autoPersistInDatabase) {
        await deleteRecord({
          variables: { id: id },
          onCompleted: () => {
            toast.success();
            onDependentsChange(dependents.filter(item => item.id !== id));
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onDependentsChange(dependents.filter(item => item.id !== id));
      }
    },
    [autoPersistInDatabase, dependents, deleteRecord, onDependentsChange],
  );

  return {
    onDependentCreate,
    onDependentUpdate,
    onDependentRemove,
  };
};
